<template>
  <Container tag="section" space-y class="px-container sm:px-container-md">
    <!-- TITLE -->
    <AnimatedContainer>
      <h2
        v-if="data.heading"
        class="slide-up mb-6 typo-heading-sm"
        v-html="data.heading"
      />

      <div
        class="slide-up mb-8 max-w-text text-gray-300 typo-body-sm md:typo-body-md"
        v-html="data.text"
      />
    </AnimatedContainer>

    <AnimatedContainer :stagger="0.075">
      <Form
        key="form"
        ref="form"
        v-slot="{ errors }"
        novalidate
        class=""
        @submit="onSubmit"
      >
        <!-- FORM FIELDS -->
        <Collapse v-slot="{ ref }" :open="state !== 'success'">
          <div :ref="ref">
            <!-- CHECKBOX GROUP -->
            <CheckboxGroup
              v-model="selectedDeliveryMethods"
              rules="required"
              :label="$t('form.delivery_method')"
              :instructions="data.delivery_method_text"
              :options="{
                email: $t('form.via_email'),
                post: $t('form.via_post'),
              }"
              class="slide-left"
              handle="delivery_method"
              :error="errors.delivery_method"
            />
            <div class="form-grid">
              <!-- NAME -->
              <InputField
                :label="$t('form.first_name')"
                rules="required"
                handle="first_name"
                :error="errors.first_name"
                autocomplete="given-name"
                class="xs:col-span-3"
              />
              <InputField
                :label="$t('form.last_name')"
                rules="required"
                handle="last_name"
                :error="errors.last_name"
                autocomplete="family-name"
                class="xs:col-span-3"
              />
            </div>

            <!-- E-MAIL -->
            <Collapse v-slot="{ ref: emailRef }" :open="emailSelected">
              <div :ref="emailRef" class="form-grid">
                <InputField
                  :label="$t('form.email')"
                  :rules="emailSelected ? 'required|email' : ''"
                  handle="email"
                  :error="errors.email"
                  autocomplete="email"
                  class="pt-6 xs:col-span-6"
                />
              </div>
            </Collapse>

            <!-- ADDRESS -->
            <Collapse v-slot="{ ref: postRef }" :open="postSelected">
              <div :ref="postRef" class="form-grid">
                <InputField
                  :label="$t('form.street')"
                  :rules="postSelected ? 'required' : ''"
                  handle="street"
                  :error="errors.street"
                  autocomplete="street-address"
                  class="pt-6 xs:col-span-6"
                />
                <InputField
                  :label="$t('form.postal_code')"
                  :rules="postSelected ? 'required|numeric' : ''"
                  handle="postal_code"
                  :error="errors.postal_code"
                  autocomplete="postal-code"
                  class="xs:col-span-2"
                />
                <InputField
                  :label="$t('form.location')"
                  :rules="postSelected ? 'required' : ''"
                  handle="location"
                  :error="errors.location"
                  autocomplete="address-level2"
                  class="xs:col-span-4"
                />
                <InputField
                  :label="$t('form.country')"
                  :rules="postSelected ? 'required' : ''"
                  handle="country"
                  :error="errors.country"
                  autocomplete="country-name"
                  class="xs:col-span-6"
                />
              </div>
            </Collapse>

            <!-- HONEYPOT -->
            <InputField handle="melassechessel" class="hidden" />

            <div class="mt-10 max-w-210">
              <AppLink
                :disabled="state === 'success'"
                type="submit"
                :is-loading="state === 'pending'"
                :show-link-arrow="true"
                class="ml-auto w-fit typo-button-md"
              >
                {{ data.button_text }}
              </AppLink>
            </div>
          </div>
        </Collapse>
        <!-- ALERT BANNER -->
        <Transition class="max-w-text typo-heading-2xs md:typo-heading-xs">
          <p v-if="state === 'success'" class="text-notification-success">
            {{ data.text_message_success }}
          </p>
          <p v-else-if="state === 'error'" class="text-notification-error">
            {{ data.text_message_error }}
          </p>
        </Transition>
      </Form>
    </AnimatedContainer>
  </Container>
</template>

<script setup lang="ts">
import { Form } from 'vee-validate'

import type { MainComponentsNewsletterRegistrationFragment } from '#gql'

defineProps<{
  data: Enforce<MainComponentsNewsletterRegistrationFragment>
}>()

const config = useRuntimeConfig()
const state = ref('idle')
const form = ref(null)
const selectedDeliveryMethods = ref(['email'])

const emailSelected = computed(() => {
  if (!Array.isArray(selectedDeliveryMethods.value)) {
    return false
  }
  return selectedDeliveryMethods.value.includes('email')
})

const postSelected = computed(() => {
  if (!Array.isArray(selectedDeliveryMethods.value)) {
    return false
  }
  return selectedDeliveryMethods.value.includes('post')
})

async function onSubmit(fields: any) {
  state.value = 'pending'

  try {
    await $fetch(
      `${config.public.BACKEND_URL}/!/forms/form_newsletter_registration`,
      {
        method: 'POST',
        body: fields,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      }
    )

    state.value = 'success'
    resetForm()
  } catch (error) {
    state.value = 'error'
    console.error(error)
  }
}

function resetForm() {
  ;(form.value as any)?.resetForm()
}
</script>

<style scoped>
.form-grid {
  @apply grid max-w-210 grid-cols-1 gap-6 xs:grid-cols-6;
}

.v-enter-active,
.v-leave-active {
  @apply transition-all delay-150 duration-500 ease-in-out;
}

.v-enter-from {
  @apply translate-x-full opacity-0;
}

.v-leave-to {
  @apply opacity-0;
}
</style>
